import { useEffect, useMemo, useRef } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Clock, Loader2, ShieldOff } from "lucide-react";
import { Skeleton } from "@nexus/ui";
import { cn } from "@nexus/ui";
import {
  userAuditLogInfiniteQueryOptions,
  accessAuditLogInfiniteQueryOptions,
  tenantAuditLogInfiniteQueryOptions,
} from "@/features/identity-hub/lib/queries/audit-log.queries";
import { relativeTimeAudit } from "@/features/identity-hub/utils/time";
import type { AuditLogItem } from "@/features/identity-hub/api/audit-log.api";

// ─── Types ────────────────────────────────────────────────────────────────────

export type TimelineScope = "user" | "access" | "tenant";

interface AuditTimelineProps {
  scope: TimelineScope;
  scopeId: string;
  /** When false, renders a "no permission" state instead of fetching. */
  hasPermission: boolean;
  /** Called when a row is clicked — used to open the global audit modal focused on that entry. */
  onEntryClick?: (entryId: string) => void;
  className?: string;
  /**
   * Per-page size. Defaults to 20 — small enough to keep the drawer audit
   * tab cheap on first paint, large enough that the average user never has
   * to scroll. Increase only if the surface is taller than the drawer.
   */
  pageSize?: number;
}

// ─── Shared scope-options builder ──────────────────────────────────────────────

// Each factory returns a different literal `queryKey` tuple
// ("user" | "access" | "tenant"), so the ternary's union type can't be
// narrowed against `useInfiniteQuery`'s overloads — but every branch produces
// the same data shape (`PaginatedAuditLogs`). We pin the call to the
// user-scope option type so consumers stay fully typed; the runtime queryKey
// is always correct because each branch is constructed by its own factory.
function auditLogScopeOptions(scope: TimelineScope, scopeId: string, pageSize: number) {
  return (
    scope === "user"   ? userAuditLogInfiniteQueryOptions(scopeId,   { pageSize }) :
    scope === "access" ? accessAuditLogInfiniteQueryOptions(scopeId, { pageSize }) :
                         tenantAuditLogInfiniteQueryOptions(scopeId, { pageSize })
  ) as ReturnType<typeof userAuditLogInfiniteQueryOptions>;
}

/**
 * Audit-tab badge count for the Person/Role/Org drawers — shares the same
 * infinite-query cache as `AuditTimeline` (below) so only the first page is
 * fetched here and no extra request is made once the tab is opened.
 *
 * Must be called unconditionally on every render, including when `scopeId`
 * is falsy (drawers render this before their `if (!entity) return null`
 * guard) — pass `enabled: false` rather than skipping the call.
 */
export function useAuditLogBadgeCount(
  scope: TimelineScope,
  scopeId: string | undefined,
  opts: { enabled: boolean },
): number | undefined {
  const { data } = useInfiniteQuery({
    ...auditLogScopeOptions(scope, scopeId ?? "", 20),
    enabled: opts.enabled && !!scopeId,
  });
  return data?.pages[0]?.items.length ?? undefined;
}

// ─── AuditTimeline ────────────────────────────────────────────────────────────

export function AuditTimeline({ scope, scopeId, hasPermission, onEntryClick, className, pageSize = 20 }: AuditTimelineProps) {
  const { t } = useTranslation();
  const baseOptions = auditLogScopeOptions(scope, scopeId, pageSize);

  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({ ...baseOptions, enabled: hasPermission && !!scopeId });

  // Single sentinel watched by IntersectionObserver. Using IO instead of the
  // shared `useInfiniteScroll` hook means we don't need to plumb the parent
  // drawer's scroll container ref through three levels of components — the
  // observer just fires whenever the sentinel becomes visible inside its
  // nearest scrolling ancestor.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    if (!hasNextPage || isFetchingNextPage) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            void fetchNextPage();
            break;
          }
        }
      },
      { rootMargin: "200px 0px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Flatten paginated items once per page-change so child rows have stable
  // references between re-renders.
  const items: AuditLogItem[] = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  );

  if (!hasPermission) {
    return (
      <div className={cn("flex flex-col items-center gap-2 py-10 text-center", className)}>
        <ShieldOff className="h-6 w-6 text-muted-foreground" />
        <p className="text-xs text-muted-foreground">
          {t("iam.auditLog.timeline.noPermission", "You don't have permission to view audit logs.")}
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={cn("flex flex-col gap-2 py-2", className)}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full rounded" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className={cn("flex flex-col items-center gap-2 py-10 text-center", className)}>
        <p className="text-xs text-muted-foreground">{t("iam.auditLog.timeline.loadError", "Failed to load audit history.")}</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="text-xs font-medium text-foreground underline-offset-2 hover:underline"
        >
          {t("iam.auditLog.retry", "Retry")}
        </button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={cn("flex flex-col items-center gap-2 py-10 text-center", className)}>
        <Clock className="h-6 w-6 text-muted-foreground" />
        <p className="text-xs text-muted-foreground">{t("iam.auditLog.timeline.empty", "No audit events yet.")}</p>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col", className)}>
      {items.map((item, i) => (
        <AuditTimelineRow
          key={item.id}
          item={item}
          isLast={i === items.length - 1}
          onClick={onEntryClick ? () => onEntryClick(item.id) : undefined}
        />
      ))}

      {/* Sentinel — IntersectionObserver auto-fires fetchNextPage when this
          enters the viewport. Also acts as the visible loading/end indicator
          so the user knows whether more events are on the way. */}
      <div
        ref={sentinelRef}
        className="flex items-center justify-center py-3 text-[11px] text-muted-foreground"
      >
        {isFetchingNextPage ? (
          <span className="inline-flex items-center gap-1.5">
            <Loader2 className="h-3 w-3 animate-spin" />
            {t("iam.auditLog.timeline.loadingMore", "Loading more…")}
          </span>
        ) : hasNextPage ? (
          <button
            type="button"
            onClick={() => void fetchNextPage()}
            className="font-medium text-foreground underline-offset-2 hover:underline"
          >
            {t("iam.auditLog.timeline.loadMore", "Load more")}
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────────

function AuditTimelineRow({ item, isLast, onClick }: { item: AuditLogItem; isLast: boolean; onClick?: () => void }) {
  const { t } = useTranslation();

  const actorName  = item.actorDisplayName || item.actorEmail || t("iam.auditLog.fallback.unknownActor");
  const meta       = item.metadata as Record<string, unknown>;
  const accessName = typeof meta["accessName"] === "string" ? meta["accessName"] : null;
  const tenantName = typeof meta["tenantName"] === "string" ? meta["tenantName"] : null;

  const verb       = t(`iam.auditLog.timeline.verb.${item.action}`, { defaultValue: item.action.replace(/_/g, " ") });

  // For create/update/delete-role and org-* events, the targetName IS the
  // subject (the role or org). For grant/revoke/invite, the role lives in
  // metadata while targetName is the affected user.
  const targetIsRole = item.action === "role_deleted" || item.action === "role_created" || item.action === "role_updated";
  const targetIsOrg  = item.action === "org_created"  || item.action === "org_updated"  || item.action === "org_deactivated";

  // For role_replaced, snapshot both role names so we can render "Editor → Admin".
  const fromAccessName = typeof meta["fromAccessName"] === "string" ? meta["fromAccessName"] : null;
  const toAccessName   = typeof meta["toAccessName"]   === "string" ? meta["toAccessName"]   : null;

  // What gets bolded right after the verb.
  const subject = (() => {
    if (item.action === "role_assigned" || item.action === "role_revoked") return accessName ?? t("iam.auditLog.fallback.aRole");
    if (item.action === "role_replaced") {
      if (fromAccessName && toAccessName) return `${fromAccessName} → ${toAccessName}`;
      return toAccessName ?? fromAccessName ?? t("iam.auditLog.fallback.aRole");
    }
    if (targetIsRole) return item.targetName ?? t("iam.auditLog.fallback.aRole");
    if (targetIsOrg)  return item.targetName ?? t("iam.auditLog.fallback.anOrg");
    if (item.action === "invite_created") return accessName ?? t("iam.auditLog.fallback.aRole");
    if (item.action === "perm_changed")   return item.targetName ?? t("iam.auditLog.fallback.aRole");
    return null;
  })();

  const showSubject = !!subject;
  const targetPreposition =
    item.action === "role_revoked"  ? t("iam.auditLog.timeline.prepositionFrom", "from ") :
    item.action === "role_replaced" ? t("iam.auditLog.timeline.prepositionOn", "on ")   :
    t("iam.auditLog.timeline.prepositionTo", "to ");

  // The secondary line shows the user the action affected, but only when that
  // user is a separate entity from the bolded subject above. For role/org
  // events the subject IS the target — there's no second person to mention.
  const showSecondaryTarget =
    !!item.targetName &&
    item.targetName !== actorName &&
    !targetIsRole &&
    !targetIsOrg;

  const Wrapper = onClick ? "button" : "div";
  const hasKnownActor = !!(item.actorDisplayName || item.actorEmail);

  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={cn(
        "group/row flex w-full items-start gap-3 py-2.5 text-left",
        !isLast && "border-b border-border/50",
        onClick && "-mx-2 rounded-md px-2 transition-colors hover:bg-muted/50",
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[12.5px] leading-snug">
          <span className={cn("font-medium", hasKnownActor ? "text-foreground" : "text-muted-foreground italic")}>
            {actorName}
          </span>
          {" "}
          <span className="text-muted-foreground">{verb}</span>
          {showSubject && (
            <>
              {" "}
              <span className="font-medium text-foreground">{subject}</span>
            </>
          )}
        </span>
        <span className="text-[11px] text-muted-foreground">
          {showSecondaryTarget && (
            <>
              <span className="text-muted-foreground/70">{showSubject ? targetPreposition : ""}</span>
              <span className="text-foreground/80">{item.targetName}</span>
              {item.targetEmail && (
                <span className="text-muted-foreground/60"> ({item.targetEmail})</span>
              )}
            </>
          )}
          {tenantName && (
            <>
              {showSecondaryTarget ? " · " : ""}
              <span className="text-muted-foreground/70">{t("iam.auditLog.timeline.inLabel", "in ")}</span>
              <span className="text-foreground/80">{tenantName}</span>
            </>
          )}
        </span>
      </div>
      <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
        {relativeTimeAudit(item.createdAt)}
      </span>
    </Wrapper>
  );
}
